import { formatCurrency } from '@erp/shared-utils';
import type {
  PurchaseTrendMonthKind,
  PurchaseTrendMonthValues,
} from '@erp/rpc';

export const PURCHASE_CURRENCY = 'CAD';

export function compactPurchaseTrendCurrency(value: number): string {
  const absolute = Math.abs(value);
  if (absolute >= 1_000_000) {
    return `${value < 0 ? '-' : ''}$${(absolute / 1_000_000).toFixed(1)}M`;
  }
  if (absolute >= 1_000) {
    return `${value < 0 ? '-' : ''}$${(absolute / 1_000).toFixed(0)}K`;
  }
  return formatCurrency(value, PURCHASE_CURRENCY);
}

export interface PurchaseTrendChartRow {
  year: number;
  month: number;
  monthLabel: string;
  purchases: number | null;
  purchasesKind: PurchaseTrendMonthKind;
  priorYearPurchases: number;
  reportedVolume: number;
  rebateEarnings: number;
  reported: number | null;
  reported_future: number | null;
  isPartialMonth: boolean;
}

export function purchaseBarAmount(
  purchases: PurchaseTrendMonthValues['purchases'],
): number | null {
  if (purchases.kind === 'forecast') {
    return purchases.forecastAmount;
  }
  return purchases.currentYearAmount;
}

function isFrozenPurchaseTrendMonth(
  year: number,
  month: number,
  now: Date,
): boolean {
  const currentYear = now.getUTCFullYear();
  const currentMonth = now.getUTCMonth() + 1;
  return year < currentYear || (year === currentYear && month < currentMonth);
}

function reportedSeriesForMonth(input: {
  rebateEarnings: number;
  frozen: boolean;
  nextFrozen: boolean | null;
}): { reported: number | null; reported_future: number | null } {
  const joinPoint = input.frozen && input.nextFrozen === false;
  return {
    reported: input.frozen ? input.rebateEarnings : null,
    reported_future: !input.frozen || joinPoint ? input.rebateEarnings : null,
  };
}

export function purchaseTrendReportedLegendIsSplit(
  rows: PurchaseTrendChartRow[],
): boolean {
  return (
    rows.some((row) => row.reported != null) &&
    rows.some((row) => row.reported_future != null)
  );
}

export const PURCHASE_TREND_LEGEND = {
  reported: 'reported',
  priorYear: 'priorYear',
  purchases: 'purchases',
  supplierPurchases: 'supplierPurchases',
} as const;

export function defaultPurchaseTrendVisibleLines(): Set<string> {
  return new Set([
    PURCHASE_TREND_LEGEND.reported,
    PURCHASE_TREND_LEGEND.priorYear,
    PURCHASE_TREND_LEGEND.purchases,
  ]);
}

export function purchaseTrendVisibleSeries(visibleLines: Set<string>): {
  purchases: boolean;
  priorYear: boolean;
  reported: boolean;
  supplierPurchases: boolean;
} {
  return {
    purchases: visibleLines.has(PURCHASE_TREND_LEGEND.purchases),
    priorYear: visibleLines.has(PURCHASE_TREND_LEGEND.priorYear),
    reported: visibleLines.has(PURCHASE_TREND_LEGEND.reported),
    supplierPurchases: visibleLines.has(
      PURCHASE_TREND_LEGEND.supplierPurchases,
    ),
  };
}

export function purchaseTrendPurchasesSeriesLabel(
  translate: (message: string, options: { context: string }) => string,
  split: boolean,
): string {
  if (split) {
    return translate('Purchases actual / forecast', {
      context: 'purchases vs rebates chart series',
    });
  }
  return translate('Purchases', {
    context: 'purchases vs rebates chart series',
  });
}

export function purchaseTrendPurchasesLegendIsSplit(
  rows: PurchaseTrendChartRow[],
): boolean {
  return (
    rows.some((row) => row.purchasesKind === 'actual') &&
    rows.some(
      (row) => row.purchasesKind === 'forecast' && row.purchases != null,
    )
  );
}

export function latestSupplierDataMonth(
  months: PurchaseTrendMonthValues[],
): { year: number; month: number } | null {
  const last = [...months].reverse().find((row) => row.reportedVolume !== 0);
  return last ? { year: last.year, month: last.month } : null;
}

export function supplierDataThroughLabel(
  months: PurchaseTrendMonthValues[],
  fullMonthNames: string[],
): string | null {
  const latest = latestSupplierDataMonth(months);
  if (!latest) {
    return null;
  }
  const monthName = fullMonthNames[latest.month - 1];
  if (!monthName) {
    return null;
  }
  return `${monthName} ${latest.year}`;
}

export function buildPurchaseTrendChartRows(
  months: PurchaseTrendMonthValues[],
  monthLabels: string[],
  now: Date = new Date(),
): PurchaseTrendChartRow[] {
  return months.map((row, index) => {
    const frozen = isFrozenPurchaseTrendMonth(row.year, row.month, now);
    const next = months[index + 1];
    const nextFrozen =
      next === undefined
        ? null
        : isFrozenPurchaseTrendMonth(next.year, next.month, now);
    const series = reportedSeriesForMonth({
      rebateEarnings: row.rebateEarnings,
      frozen,
      nextFrozen,
    });
    return {
      year: row.year,
      month: row.month,
      monthLabel: monthLabels[row.month - 1] ?? String(row.month),
      purchases: purchaseBarAmount(row.purchases),
      purchasesKind: row.purchases.kind,
      priorYearPurchases: row.purchases.priorYearAmount,
      reportedVolume: row.reportedVolume,
      rebateEarnings: row.rebateEarnings,
      reported: series.reported,
      reported_future: series.reported_future,
      isPartialMonth: row.purchases.isPartialMonth,
    };
  });
}

function findPartialThroughMonth(
  months: PurchaseTrendMonthValues[],
): number | null {
  const partial = months.find((row) => row.purchases.isPartialMonth);
  return partial?.month ?? null;
}

export function lastUploadThroughMonth(
  months: PurchaseTrendMonthValues[],
): number | null {
  const partial = findPartialThroughMonth(months);
  if (partial !== null) {
    return partial;
  }
  const withPurchases = [...months]
    .reverse()
    .find((row) => row.purchases.currentYearAmount !== 0);
  return withPurchases?.month ?? null;
}

export function forecastMonthsWithAmount(
  months: PurchaseTrendMonthValues[],
): number[] {
  return months
    .filter(
      (row) =>
        row.purchases.kind === 'forecast' &&
        row.purchases.forecastAmount !== null,
    )
    .map((row) => row.month);
}

export interface PurchaseTrendSubtitleModel {
  lastActualMonthName: string | null;
  forecastFromName: string | null;
  forecastToName: string | null;
  includePriorYear: boolean;
}

export function buildPurchaseTrendSubtitle(input: {
  monthNames: string[];
  lastActualMonth: number | null;
  forecastMonths: number[];
  includePriorYear: boolean;
}): PurchaseTrendSubtitleModel {
  const firstForecast = input.forecastMonths[0];
  const lastForecast = input.forecastMonths[input.forecastMonths.length - 1];
  return {
    lastActualMonthName:
      input.lastActualMonth === null
        ? null
        : (input.monthNames[input.lastActualMonth - 1] ?? null),
    forecastFromName:
      firstForecast === undefined
        ? null
        : (input.monthNames[firstForecast - 1] ?? null),
    forecastToName:
      lastForecast === undefined
        ? null
        : (input.monthNames[lastForecast - 1] ?? null),
    includePriorYear: input.includePriorYear,
  };
}
